import { ExternalLinkIcon } from '@chakra-ui/icons';
import {
  Code,
  Flex,
  Skeleton,
  SkeletonText,
  Text,
  Td,
  Table,
  Tbody,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import {
  RootStateOrAny,
  useSelector,
} from 'react-redux';
import {
  Link,
  useHistory,
  useParams,
} from 'react-router-dom';

import useRequest from 'shared/src/hooks/useRequest';
import ActionMenu from 'web-react-ui/src/components/actionMenu/ActionMenu';
import PostStatsSingle from 'shared/src/components/PostStatsSingle';
import PageBackButton from 'web-react-ui/src/chakra/page/PageBackButton';
import PageHeader from 'web-react-ui/src/chakra/page/PageHeader';
import PageLayout from 'web-react-ui/src/chakra/page/PageLayout';
import Business from 'web-react-ui/src/types/Business.interface';
import StatusBadge from 'shared/src/components/StatusBadge';
import Post from 'shared/src/interfaces/Post.interface';
import { getPostPageTitle } from "shared/src/components/getPostPageTitle";
import businessModule from '../../../../modules/businesses';
import client from '../../../../services/client';
import SectionHeader from '../components/SectionHeader';
import Content from './ContentSection';
import DeleteModal from './DeleteModal';
import Preview from './PreviewSection';
import { recordSocialPostFeedback } from '../../../../services/firebase';

const fetchPost = async ({
  businessId,
  postId,
}: {
  businessId: string;
  postId: string;
}): Promise<Post> => {
  return client.businesses.for(businessId).social.posts.for(postId).details();
};

const schedulePost = async ({
  businessId,
  postId,
}: {
  businessId: string;
  postId: string;
}): Promise<void> => {
  return client.businesses.for(businessId).social.posts.for(postId).update({ state: 'scheduled' });
};

const deletePost = async (
  { formValues, business, postId, history }:
  {
    formValues: { reason: string, details?: string },
    business: Business;
    postId: string,
    history: ReturnType<typeof useHistory>
  },
): Promise<void> => {
  // Delete first...
  await client.businesses.for(business.id).social.posts.for(postId).delete();

  const logPayload = {
    date: new Date().toISOString(),
    businessName: business.name,
    reason: formValues.reason,
    details: formValues.details,
    link: `https://admin.loopmediagroup.com/businesses/${business.id}/social-ai/post/${postId}`,
  };
  await recordSocialPostFeedback(logPayload);

  history.replace('../social');
};

const PostDetails = () => {
  const history = useHistory();
  const params = useParams<{ postId: string }>();
  const business = useSelector((state: RootStateOrAny) => businessModule.selectors.business.getData(state));
  const postRequest = useRequest(fetchPost, { businessId: business.id, postId: params.postId });
  const post: Post = postRequest.result;

  const toast = useToast();
  const deleteDisclosure = useDisclosure(false);

  const canEdit = ['scheduled', 'draft'].includes(post?.state);
  const canDelete = ['scheduled', 'draft'].includes(post?.state);
  const canSchedule = ['draft'].includes(post?.state);

  const actions = useMemo(
    () => [
      {
        label: 'Edit',
        hidden: !canEdit,
        extraProps: {
          as: Link,
          to: `./${params.postId}/edit`,
        },
      },
      {
        label: 'Schedule',
        hidden: !canSchedule,
        action: async () => {
          try {
            await schedulePost({ businessId: business.id, postId: params.postId });
          } catch (error) {
            toast({
              title: 'Failed to schedule.',
              description: 'Check that the scheduled date is not in the past.',
              status: 'error',
              isClosable: true,
            });
            return;
          }
          // Wait for indexing...
          await new Promise(resolve => setTimeout(resolve, 1500));
          postRequest.run({ businessId: business.id, postId: params.postId });
        },
      },
      {
        label: 'Delete',
        hidden: !canDelete,
        action: async () => {
          deleteDisclosure.onOpen();
        },
      },
    ],
    [params.postId, post, canEdit, canDelete],
  );

  const pageTitle = getPostPageTitle(post);

  return (
    <PageLayout>
      <PageBackButton to="../social">Back</PageBackButton>
      <Skeleton isLoaded={!!post}>
        <PageHeader controls={<ActionMenu actions={actions} />}>{pageTitle}</PageHeader>
      </Skeleton>
      <Flex wrap="wrap" gap="3em">
        <Flex direction="column" gap="3em" flex="1" minW="40ch" alignItems="start">
          <Details post={post} />
          <PostStatsSingle
            businessId={business.id}
            post={post}
            alignSelf="stretch"
          />
          <SectionHeader>Content</SectionHeader>
          <Content post={post} />
          <Stats post={post} />
        </Flex>
        <Preview post={post} businessId={business.id} />
        <DeleteModal
          post={post}
          isOpen={deleteDisclosure.isOpen}
          onClose={deleteDisclosure.onClose}
          onDelete={(formValues) => deletePost({
            formValues,
            business,
            postId: params.postId,
            history,
          })}
        />
      </Flex>
    </PageLayout>
  );
};

const Details = ({ post }: { post: Post }) => {
  return (
    <Table variant="unstyled" w="auto">
      <Tbody>
        <Tr>
          <Td pt={0} pl={0}>
            Schedule Date
          </Td>
          <Td pt={0}>
            <SkeletonText isLoaded={!!post} noOfLines={1} minW="6em">
              <Text>{post?.scheduledDate}</Text>
            </SkeletonText>
          </Td>
        </Tr>
        <Tr>
          <Td pt={0} pl={0}>
            Status
          </Td>
          <Td pt={0}>
            <SkeletonText isLoaded={!!post} noOfLines={1} minW="6em">
              <Flex gap="1em" align="center">
                <StatusBadge status={post?.displayState} />
                {post?.socialNetworkUrl ? (
                  <a href={post.socialNetworkUrl} target="_blank" rel="noreferrer">
                    Go To Post <ExternalLinkIcon />
                  </a>
                ) : undefined}
                {!!post?.errorCode && (
                  <details>
                    <summary>
                      <Code>{post.errorCode}</Code>
                    </summary>
                    <Code>{post.errorMessage}</Code>
                  </details>
                )}
              </Flex>
            </SkeletonText>
          </Td>
        </Tr>
      </Tbody>
    </Table>
  );
};

const Stats = ({ post }: { post: Post }) => {
  if (!post) {
    return <Skeleton w="100%" h="2em" />;
  }

  return <></>;
};

export default PostDetails;
